import React from 'react';
import { Box, Typography, Paper, LinearProgress, Chip } from '@mui/material';
import {
  Share as ShareIcon,
  Toll as CreditIcon,
  EmojiEvents as TrophyIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { EducationModal, type EducationStep } from '../shared/EducationModal';

// Visual: Referral link concept
const ReferralLinkVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" fontFamily="monospace">squareup.app/r/</Typography>
      <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">YOURCODE</Typography>
      <CopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
    </Paper>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      Share this link with friends
    </Typography>
  </Box>
);

// Visual: How it works
const HowItWorksVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
    {[
      { step: '1', label: 'Share Link', icon: '🔗' },
      { step: '2', label: 'Friend Joins', icon: '👋' },
      { step: '3', label: 'You Earn', icon: '🎉' },
    ].map((item, i) => (
      <React.Fragment key={item.step}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5">{item.icon}</Typography>
          <Typography variant="caption" display="block">{item.label}</Typography>
        </Box>
        {i < 2 && <Typography variant="body2" color="text.secondary">→</Typography>}
      </React.Fragment>
    ))}
  </Box>
);

// Visual: Tier rewards
const TierRewardsVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
    {[
      { tier: 1, refs: 3, credits: 1, unlocked: true },
      { tier: 2, refs: 5, credits: 2, unlocked: true },
      { tier: 3, refs: 10, credits: 5, unlocked: false },
    ].map((t) => (
      <Paper
        key={t.tier}
        elevation={0}
        sx={{
          p: 1,
          textAlign: 'center',
          bgcolor: t.unlocked ? 'success.50' : 'grey.100',
          border: 1,
          borderColor: t.unlocked ? 'success.200' : 'grey.300',
          minWidth: 60,
          opacity: t.unlocked ? 1 : 0.7,
        }}
      >
        <Typography variant="caption" fontWeight="bold" display="block">Tier {t.tier}</Typography>
        <Chip
          icon={<CreditIcon sx={{ fontSize: 12 }} />}
          label={`+${t.credits}`}
          size="small"
          color={t.unlocked ? 'success' : 'default'}
          sx={{ mt: 0.5, height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 9 }}>
          {t.refs} refs
        </Typography>
      </Paper>
    ))}
  </Box>
);

// Visual: Progress tracking
const ProgressVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center', maxWidth: 200, mx: 'auto' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption">Your Progress</Typography>
      <Typography variant="caption" fontWeight="bold">4 / 5</Typography>
    </Box>
    <LinearProgress variant="determinate" value={80} color="primary" sx={{ height: 8, borderRadius: 1, mb: 1 }} />
    <Typography variant="caption" color="text.secondary">
      1 more referral to unlock Tier 2!
    </Typography>
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Referral Program',
    description: 'Earn free credits by inviting friends! Share your unique referral link and get rewarded when they join.',
    icon: <ShareIcon sx={{ fontSize: 40 }} />,
    visual: <ReferralLinkVisual />,
  },
  {
    title: 'How It Works',
    description: 'It\'s simple: share your link, your friend signs up using it, and you earn credits. They get to join the fun, you get free boards!',
    icon: <CreditIcon sx={{ fontSize: 40 }} />,
    visual: <HowItWorksVisual />,
  },
  {
    title: 'Tier Rewards',
    description: 'The more friends you refer, the more you earn! Unlock higher reward tiers as your referral count grows.',
    icon: <TrophyIcon sx={{ fontSize: 40 }} />,
    visual: <TierRewardsVisual />,
  },
  {
    title: 'Track Progress',
    description: 'See your total referrals, credits earned, and progress toward the next reward tier. Keep sharing to maximize rewards!',
    icon: <ShareIcon sx={{ fontSize: 40 }} />,
    visual: <ProgressVisual />,
  },
];

interface ReferralsEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const ReferralsEducationModal: React.FC<ReferralsEducationModalProps> = ({ open, onClose }) => (
  <EducationModal
    open={open}
    onClose={onClose}
    modalId="referrals"
    steps={steps}
    headerColor="info.main"
    finishButtonText="View Referrals"
    buttonColor="info"
  />
);

export default ReferralsEducationModal;
